import styles from './toggleButtonGroup.module.scss';

export type ToggleButtonOption<T extends string | number = string> = {
  readonly value: T;
  readonly label: string;
};

export type ToggleButtonGroupProps<T extends string | number = string> = {
  /** Array of options to display */
  options: readonly ToggleButtonOption<T>[];
  /** Currently selected value */
  value: T;
  /** Called when selection changes */
  onChange: (value: T) => void;
  /** Optional aria-label for accessibility */
  'aria-label'?: string;
  /** Additional class name */
  className?: string;
};

/**
 * A group of toggle buttons for selecting from a set of options.
 * Commonly used for date range selection in admin dashboards.
 *
 * @example
 * ```tsx
 * <ToggleButtonGroup
 *   options={[
 *     { value: 7, label: '7 Days' },
 *     { value: 14, label: '14 Days' },
 *     { value: 30, label: '30 Days' },
 *   ]}
 *   value={days}
 *   onChange={setDays}
 *   aria-label="Select date range"
 * />
 * ```
 */
export function ToggleButtonGroup<T extends string | number = string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className
}: ToggleButtonGroupProps<T>) {
  return (
    <div
      className={`${styles.group} ${className ?? ''}`}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={`${styles.button} ${option.value === value ? styles.active : ''}`}
          onClick={() => onChange(option.value)}
          aria-pressed={option.value === value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
